import { useCallback, useContext, useEffect, useState } from 'react';
import { PHOTO_TYPE, PhotoTypes, SchoolDetails, TeacherDetails } from './types';
import { Svg } from '../common/Svg';
import { FavoriteHeart } from '../common/favoriteHeart';
import { getFavList } from '../favorites/favoritesStorage';
import { TeacherPageContext, ViewMode } from './TeacherPageContextProvider';
import EditClassroomDescription from './EditClassroomDescription';
import { LightBoxWrapper } from '../common/LightBoxWrapper';
import { PhotoUploadConfig } from '../common/photoupload/types';

interface TeacherProfileAboutSectionProps {
    teacherDetails: TeacherDetails,
    schoolDetails: SchoolDetails,
    pageUrl: string,
    vanityUrl: string,
    setUploadModalPhotoType: (type: PHOTO_TYPE) => void,
}

const FALLBACK_CLASSROOM_PHOTO = `${baseURL}images/classroom/fallback-classroom@2x.png`;

const TeacherProfileAboutSection = ({
    vanityUrl,
    pageUrl,
    teacherDetails,
    schoolDetails,
    setUploadModalPhotoType,
}:TeacherProfileAboutSectionProps) => {
    const teacherPageContext = useContext(TeacherPageContext);
    const normalizeDescription = (value: string | null | undefined) =>
        (!value || value === 'null') ? '' : value;
    const normalizedTeacherDescription = normalizeDescription(teacherDetails.classroomDescription);
    const [isTeacherFavorited, setIsTeacherFavorited] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [shouldCloseDescriptionLightbox, setShouldCloseDescriptionLightbox] = useState(false);
    const [classroomDescription, setClassroomDescription] = useState(normalizedTeacherDescription);
    const [draftClassroomDescription, setDraftClassroomDescription] = useState(normalizedTeacherDescription);
    const [formClassroomDescriptionError, setFormClassroomDescriptionError] = useState<string>('');
    const [formClassroomDescriptionSuccessMessage, setFormClassroomDescriptionSuccessMessage] = useState<string>('');
    useEffect(() => {
        $.when(getFavList('favTeacherIds')).then(function (favList) {
            if ($.inArray(teacherDetails.id, favList) > -1) {
                setIsTeacherFavorited(true);
            }
        });
    }, [teacherDetails.id]);

    const isOwnerTeacherView = teacherPageContext.viewMode === ViewMode.TEACHER && teacherPageContext.isOwner;

    const openDescriptionEditor = () => {
        setDraftClassroomDescription(classroomDescription);
        setFormClassroomDescriptionError('');
        setFormClassroomDescriptionSuccessMessage('');
        setIsEditingDescription(true);
    };

    const handleSaveDescription = useCallback(() => {
        setFormClassroomDescriptionError('');
        setFormClassroomDescriptionSuccessMessage('');

        $.ajax(`${secureBaseURL}teacher/profile-desc.html`, {
            method: 'POST',
            data: { profileDesc: draftClassroomDescription },
            xhrFields: { withCredentials: true },
            success: () => {
                setClassroomDescription(draftClassroomDescription);
                setShouldCloseDescriptionLightbox(true);
            },
            error: () => {
                setFormClassroomDescriptionError('Error updating classroom description, please try again.');
            },
        });
    }, [draftClassroomDescription]);

    const handleCancelDescription = () => {
        setFormClassroomDescriptionError('');
        setShouldCloseDescriptionLightbox(true);
    };

    const renderHbcuAlum = () => {
        if (!teacherDetails.hbcuGrad) {
            return null;
        }
        return <span className="hbcu">HBCU Alum</span>;
    };

    const renderPovertyLevel = () => {
        if (!schoolDetails.schoolPoverty) {
            return null;
        }
        return (
            <div className="economic-descriptor">
                <p>{schoolDetails.schoolPoverty}
                    <span className="tooltip-wrapper">
                        <Svg icon="help" additionalClasses="populate-tooltip"/>
                        <span className="hover-tooltip">
                            Data about students&apos; economic need comes from the National Center for Education Statistics,
                            via our partners at MDR Education. <a href="https://help.donorschoose.org/hc/en-us/articles/202375748-How-is-a-school-s-economic-need-level-defined-at-DonorsChoose-">Learn more</a>
                        </span>
                    </span>
                </p>
            </div>
        );
    };

    const renderDonorStats = () => {
        if (!teacherDetails.totalDonors) {
            return null;
        }
        const donors = teacherDetails.totalDonors;
        return (
            <span className="donors">
                <strong>{donors}</strong> {donors === 1 ? 'donor' : 'donors'}
            </span>
        );
    };

    const renderFundedProjectsStatus = () => {
        const numFundedProjects = teacherDetails.numFundedProjects;
        if (numFundedProjects !== 0) {
            return (
                <span className="funded-projects">
                    <strong>{numFundedProjects}</strong>{numFundedProjects === 1 ? 'project' : 'projects'} funded
                </span>
            );
        }
        return <span className="nbf">Never Before Funded</span>;
    };

    const renderVanityUrl = () => {
        if (teacherPageContext.isOwner && vanityUrl) {
            const viewableString = pageUrl.replace(/^https?:\/\/[^.]*./, '');
            return (<li>
                <a href={pageUrl}>
                    <Svg icon="link" attributes={{ role: 'presentation' }} additionalClasses="standalone"/>
                    <ul className="teacher-page-link">
                        <li className="link">
                            {viewableString}
                        </li>
                    </ul>
                </a>
            </li>);
        }
    };

    const renderMoreAboutSection = () => {
        return (
            <>
                <h4>More about {teacherDetails.name}</h4>
                {renderPovertyLevel()}
                {renderHbcuAlum()}
                {renderFundedProjectsStatus()}
                {renderDonorStats()}
                {renderVanityUrl()}
            </>
        );
    };

    const renderFollowButton = () => {
        return (
            <FavoriteHeart
                favoriteType="teacherId"
                pageURL={pageUrl}
                favoriteValue={teacherDetails.id}
                isFavorite={isTeacherFavorited}
                trackingName="Follow-teacher"
                activeText="Following!"
                inactiveText={'Follow ' + teacherDetails.name + ' for updates'}
                formDivClassName="save-teacher-popup"
                additionalButtonClasses="favorite-marker"
                buttonText="Get updates"
                key="favorite-marker"
            />
        );
    };

    const renderClassroomDescription = () => {
        const fallbackDescription = 'Students across the country come to school each day eager to learn, grow, and reach their full potential. Every classroom thrives when students have access to the supplies and resources that bring lessons to life, spark curiosity, and build confidence. Thanks to generous donors like you, students gain the tools they need to succeed in the classroom and beyond.';
        return (<div className="classroom-description-section">
            {isOwnerTeacherView && !classroomDescription
                ? (<div className="classroom-description-empty">
                    <p>Tell donors a little about your classroom.</p>
                    <p className="empty-hint">Share who your students are, what they love to learn, and what makes your classroom special.</p>
                    <button type="button"
                        className="teacher-profile-edit-empty-cta button-secondary small"
                        onClick={openDescriptionEditor}>
                        Write a classroom description
                    </button>
                </div>)
                : (<div className="classroom-description" style={{ display: 'block' }}>
                    {isOwnerTeacherView
                        ? classroomDescription
                        : (normalizedTeacherDescription || fallbackDescription)}
                </div>)}
        </div>);
    };

    const renderPhotoEditButton = (photoType: PHOTO_TYPE) => {
        return (isOwnerTeacherView &&
                <button type="button"
                    onClick={() => setUploadModalPhotoType(photoType)}
                    className="teacher-profile-edit-button teacher-profile-edit-photo js-classroom-photo-upload js-admin"
                    aria-label={'Edit classroom photo'}>
                    <Svg icon="write" iconFamily="junior"/>
                </button>
        );
    };

    const renderClassroomPhoto = () => {
        return (<div className={'classroom-photo js-classroom-photo-holder'}>
            {renderPhotoEditButton(PhotoTypes.CLASSROOM)}
            <img src={teacherDetails.classroomPhotoUrl ? teacherDetails.classroomPhotoUrl : FALLBACK_CLASSROOM_PHOTO} alt=""/>
        </div>);
    };

    return (
        <div className="teacher-profile-about-sections">
            <div className="container">
                <div className="classroom-description-container">
                    <div>
                        {normalizedTeacherDescription || isOwnerTeacherView
                            ? (<h4 className="about-classroom-heading">
                                About this classroom
                                {isOwnerTeacherView && classroomDescription && (
                                    <button type="button"
                                        className="teacher-profile-edit-button"
                                        aria-label="Edit classroom description"
                                        onClick={openDescriptionEditor}>
                                        <span>Edit description</span>
                                    </button>
                                )}
                            </h4>)
                            : <h4>About DonorsChoose classrooms</h4>
                        }
                        {renderClassroomDescription()}
                        {teacherPageContext.viewMode !== ViewMode.TEACHER && !teacherPageContext.isOwner &&
                            renderFollowButton()
                        }
                    </div>
                    <div>
                        <div className="placeholder-classroom-photo">
                            {renderClassroomPhoto()}
                        </div>
                    </div>
                    <div className="hide-for-now">
                        {renderMoreAboutSection()}
                    </div>
                </div>
            </div>
            {isEditingDescription && (
                <LightBoxWrapper
                    fixed={true}
                    closeLightbox={shouldCloseDescriptionLightbox}
                    unMountHandler={() => {
                        setShouldCloseDescriptionLightbox(false);
                        setIsEditingDescription(false);
                    }}>
                    <div className="teacher-profile-edit-lightbox">
                        <EditClassroomDescription
                            classroomDescription={draftClassroomDescription}
                            handleInputChange={(event:React.ChangeEvent<HTMLTextAreaElement>) => setDraftClassroomDescription(event.target.value)}
                            handleClassroomDescriptionChange={() => { /* no-op: save happens via Save button */ }}
                            formError={formClassroomDescriptionError}
                            formSuccessMessage={formClassroomDescriptionSuccessMessage}
                        />
                        <div className="teacher-profile-edit-actions">
                            <button type="button"
                                className="teacher-profile-edit-save button-primary small"
                                onClick={handleSaveDescription}>
                                Save
                            </button>
                            <button type="button"
                                className="teacher-profile-edit-cancel button-secondary small"
                                onClick={handleCancelDescription}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </LightBoxWrapper>
            )}
        </div>
    );
};

export default TeacherProfileAboutSection;
